import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { dashboardAPI, projectsAPI, tasksAPI, eventsAPI, applicantsAPI } from '../../lib/api';
import { 
  Users, UserPlus, Briefcase, CheckSquare, CalendarBlank, 
  CurrencyDollar, Warning, TrendingUp 
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const StatCard = ({ icon: Icon, title, value, link, color = 'text-[#7B1818]' }) => (
  <Link 
    to={link}
    className="bg-white border border-[#D8D2C4] p-6 hover:border-[#C5A059] transition-colors"
    data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-[#8A847A] mb-1">{title}</p>
        <p className="text-3xl font-heading text-[#1A1614]">{value}</p>
      </div>
      <div className={`p-3 bg-[#F5F2EB] ${color}`}>
        <Icon size={24} />
      </div>
    </div>
  </Link>
);

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, activityRes, projectsRes, eventsRes, tasksRes] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getRecentActivity(),
          projectsAPI.getAll({ status: 'in_progress' }),
          eventsAPI.getAll(),
          tasksAPI.getAll({ status: 'pending' })
        ]);
        setStats(statsRes.data);
        setRecentActivity(activityRes.data.slice(0, 10));
        setRecentProjects(projectsRes.data.slice(0, 5));
        setUpcomingEvents(eventsRes.data.filter(e => new Date(e.date) >= new Date()).slice(0, 5));
        setOverdueTasks(tasksRes.data.filter(t => t.due_date && new Date(t.due_date) < new Date()).slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl text-[#1A1614]">Dashboard</h1>
          <p className="text-[#8A847A]">Overview of Grand Priory operations</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            icon={Users} 
            title="Active Members" 
            value={stats?.active_members || 0}
            link="/admin/members"
          />
          <StatCard 
            icon={UserPlus} 
            title="Pending Applicants" 
            value={stats?.pending_applicants || 0}
            link="/admin/applicants"
            color="text-[#C5A059]"
          />
          <StatCard 
            icon={Briefcase} 
            title="Active Projects" 
            value={stats?.active_projects || 0}
            link="/admin/projects"
          />
          <StatCard 
            icon={CheckSquare} 
            title="Overdue Tasks" 
            value={stats?.overdue_tasks || 0}
            link="/admin/tasks"
            color="text-red-600"
          />
        </div>

        {/* Second Row Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard 
            icon={CalendarBlank} 
            title="Upcoming Events" 
            value={stats?.upcoming_events || 0}
            link="/admin/events"
          />
          <StatCard 
            icon={CurrencyDollar} 
            title="Total Contributions" 
            value={`$${(stats?.total_contributions || 0).toLocaleString()}`}
            link="/admin/contributions"
            color="text-green-600"
          />
          <StatCard 
            icon={Users} 
            title="Total Members" 
            value={stats?.total_members || 0}
            link="/admin/members"
          />
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Active Projects */}
          <div className="bg-white border border-[#D8D2C4] p-6" data-testid="active-projects-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl text-[#1A1614]">Active Projects</h2>
              <Link to="/admin/projects" className="text-sm text-[#7B1818] hover:underline">
                View All
              </Link>
            </div>
            {recentProjects.length > 0 ? (
              <div className="space-y-3">
                {recentProjects.map(project => (
                  <div 
                    key={project.id} 
                    className="flex items-center justify-between py-2 border-b border-[#E8E4D9] last:border-0"
                  >
                    <div>
                      <p className="font-medium text-[#1A1614]">{project.title}</p>
                      <p className="text-sm text-[#8A847A] capitalize">{project.status?.replace('_', ' ')}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs ${
                      project.priority === 'high' ? 'bg-red-100 text-red-700' :
                      project.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {project.priority}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#8A847A] text-center py-4">No active projects</p>
            )}
          </div>

          {/* Upcoming Events */}
          <div className="bg-white border border-[#D8D2C4] p-6" data-testid="upcoming-events-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl text-[#1A1614]">Upcoming Events</h2>
              <Link to="/admin/events" className="text-sm text-[#7B1818] hover:underline">
                View All
              </Link>
            </div>
            {upcomingEvents.length > 0 ? (
              <div className="space-y-3">
                {upcomingEvents.map(event => (
                  <div 
                    key={event.id} 
                    className="flex items-center gap-4 py-2 border-b border-[#E8E4D9] last:border-0"
                  >
                    <div className="w-12 h-12 bg-[#7B1818] text-white flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold">{new Date(event.date).getDate()}</span>
                      <span className="text-xs">{new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}</span>
                    </div>
                    <div>
                      <p className="font-medium text-[#1A1614]">{event.title}</p>
                      <p className="text-sm text-[#8A847A]">{event.location || 'Location TBD'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#8A847A] text-center py-4">No upcoming events</p>
            )}
          </div>

          {/* Overdue Tasks */}
          {overdueTasks.length > 0 && (
            <div className="bg-white border border-red-200 p-6" data-testid="overdue-tasks-card">
              <div className="flex items-center gap-2 mb-4">
                <Warning size={20} className="text-red-600" />
                <h2 className="font-heading text-xl text-[#1A1614]">Overdue Tasks</h2>
              </div>
              <div className="space-y-3">
                {overdueTasks.map(task => (
                  <div 
                    key={task.id} 
                    className="flex items-center justify-between py-2 border-b border-[#E8E4D9] last:border-0"
                  >
                    <div>
                      <p className="font-medium text-[#1A1614]">{task.title}</p>
                      <p className="text-sm text-red-600">
                        Due: {new Date(task.due_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          <div className="bg-white border border-[#D8D2C4] p-6" data-testid="recent-activity-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl text-[#1A1614]">Recent Activity</h2>
              <Link to="/admin/audit-logs" className="text-sm text-[#7B1818] hover:underline">
                View All
              </Link>
            </div>
            {recentActivity.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.map((log, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 py-2 border-b border-[#E8E4D9] last:border-0"
                  >
                    <div className="w-2 h-2 mt-2 rounded-full bg-[#C5A059]" />
                    <div>
                      <p className="text-sm text-[#1A1614]">
                        <span className="capitalize">{log.action?.replace('_', ' ')}</span>
                        {' - '}
                        <span className="text-[#8A847A]">{log.entity_type}</span>
                      </p>
                      <p className="text-xs text-[#8A847A]">
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#8A847A] text-center py-4">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
